import { FetcherOptions } from '../../../../components/Hooks/useList.types';
import { SaleItemType } from '../../../../components/Search/interfaces/product-action.dto';
import { saleItemIsKit } from '../../../catalogs/utils/saleItemType.utils';
import {
  OfflineDatabaseCatalogSection,
  OfflineDatabaseCatalogSectionKit,
  OfflineDatabaseCatalogSectionProduct,
} from '../../database/offlineDatabase/types/offlineDatabaseCatalog.types';
import { OfflineDatabaseProductColorPriceInfoV2 } from '../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { offlineClassificationsRepository } from '../../repositories/offlineClassifications.repository';
import { offlineKitsRepository } from '../../repositories/offlineKits.repository';
import { offlineProductColorPriceInfoRepository } from '../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../repositories/offlineProducts.repository';
import OfflinePriceTableUtils from '../../repositories/utils/offlinePriceTable.utils';
import {
  SectionWithItem,
  SectionWithItemAndInfo,
} from '../../transformers/types/OfflineDatabaseCatalog.transformers.types';
import {
  GetItemForProductParams,
  GetItemsToMountParams,
  PaginateValidCatalogSectionProductsParams,
} from '../types/getESProductCatalogSearch.utils.types';

class GetCatalogItemSearchUtils {
  static async paginateValidCatalogSectionProducts({
    options,
    offlineCatalog,
    offlineSectionProductSet,
  }: PaginateValidCatalogSectionProductsParams): Promise<SectionWithItemAndInfo[]> {
    const itemsToMount = this.getItemsToMount({
      options,
      offlineCatalog,
      offlineSectionProductSet,
    });

    if (!itemsToMount.length) {
      return [];
    }

    const priceTableId = await OfflinePriceTableUtils.getOrderOrDefaultPriceTableId(
      options.params?.orderId,
    );

    const pagedItems: SectionWithItemAndInfo[] = [];

    for (const item of itemsToMount) {
      const pagedItem = saleItemIsKit(item.sectionItem)
        ? await this.getItemForKit(item.sectionItem, item.section)
        : await this.getItemForProduct({
            item: item.sectionItem,
            section: item.section,
            priceTableId,
          });

      pagedItems.push(pagedItem);
    }

    return pagedItems;
  }

  private static async getItemForProduct({ item, section, priceTableId }: GetItemForProductParams) {
    const {
      product,
      productColor,
      allProductColors,
      productColorPriceInfo,
      productColorClassifications,
    } = await this.getProductInfo(item, priceTableId);

    const itemForSectionItem = {
      section,
      sectionItem: item,
      type: SaleItemType.PRODUCT_COLOR as const,
      product,
      productColor,
      allProductColors,
      productColorPriceInfo: productColorPriceInfo as OfflineDatabaseProductColorPriceInfoV2,
      productColorClassifications,
    };

    return itemForSectionItem;
  }

  private static async getItemForKit(
    item: OfflineDatabaseCatalogSectionKit,
    section: OfflineDatabaseCatalogSection,
  ) {
    const kit = await this.getKitInfo(item);
    const itemForSectionItem = {
      section,
      kit: kit,
      type: SaleItemType.KIT as const,
      sectionItem: item,
    };

    return itemForSectionItem;
  }

  private static getItemsToMount({
    options,
    offlineCatalog,
    offlineSectionProductSet,
  }: GetItemsToMountParams): SectionWithItem[] {
    const { limit, skip } = this.getParamsFromOptions(options);

    const itemsToMount: SectionWithItem[] = [];

    let itemsCount = 0;

    for (const section of offlineCatalog.sections) {
      for (const sectionItem of section.items) {
        const mustSkipSectionProduct = !offlineSectionProductSet.has(sectionItem.id);
        if (mustSkipSectionProduct) {
          continue;
        }

        const mustMountItem = itemsCount >= skip && itemsToMount.length < limit;
        if (mustMountItem) {
          itemsToMount.push({ section, sectionItem });
        }

        itemsCount++;

        const hasAchievedLimit = itemsToMount.length >= limit;
        if (hasAchievedLimit) {
          return itemsToMount;
        }
      }
    }

    return itemsToMount;
  }

  private static getParamsFromOptions(options: FetcherOptions) {
    const limit = options.count;
    const skip = options.page * limit;
    return { limit, skip };
  }

  private static async getKitInfo(item: OfflineDatabaseCatalogSectionKit) {
    const kit = await offlineKitsRepository.getByIdOrFail(item.kit.id);

    return kit;
  }

  private static async getProductInfo(
    item: OfflineDatabaseCatalogSectionProduct,
    priceTableId: string,
  ) {
    const [product, allProductColors, productColorPriceInfo, productColorClassifications] =
      await Promise.all([
        offlineProductsRepository.getByIdOrFail(item.product.id),
        offlineProductColorsRepository.findByProductId(item.product.id),
        offlineProductColorPriceInfoRepository.getByIdOrFail(item.productColor.id, priceTableId),
        offlineClassificationsRepository.getClassificationsToUseAsTagByIds(
          item.filterOptions.classificationIds,
        ),
      ]);
    const productColor = allProductColors?.find((pc) => pc.id === item.productColor.id);

    if (!productColor) {
      throw new Error(
        `Product color with ID ${item.productColor.id} not found for product ID ${item.product.id}`,
      );
    }

    return {
      product,
      allProductColors,
      productColorPriceInfo,
      productColorClassifications,
      productColor,
    };
  }
}

export default GetCatalogItemSearchUtils;
